import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ParentChild } from "../types/parentFamily";
import { useAuth } from "../context/AuthContext";
import { familyQueryKey, loadFamilyMembers } from "./family/familyQuery";
import { useFamilyMutations } from "./family/useFamilyMutations";

export { familyQueryKey } from "./family/familyQuery";

export function useParentFamilyData(
  orgId: string,
  userId: string,
  enabled = true
) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: familyQueryKey(orgId, userId),
    queryFn: async () => {
      if (!orgId || !userId || !token) {
        throw new Error("Missing organization or auth token");
      }
      return loadFamilyMembers(orgId, userId, token);
    },
    enabled: !!orgId && !!userId && !!token && enabled,
    staleTime: 30_000,
  });

  const children = useMemo(() => query.data ?? [], [query.data]);
  const isHydrated = query.isFetched || !orgId || !userId || !token;

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: familyQueryKey(orgId, userId),
      }),
      queryClient.invalidateQueries({
        queryKey: ["family-search-index", orgId],
      }),
    ]);
  }, [queryClient, orgId, userId]);

  const setChildrenCache = useCallback(
    (updater: (prev: ParentChild[]) => ParentChild[]) => {
      queryClient.setQueryData<ParentChild[]>(
        familyQueryKey(orgId, userId),
        (prev) => updater(prev ?? [])
      );
    },
    [queryClient, orgId, userId]
  );

  const runMutation = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      setError(null);
      try {
        return await fn();
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Family request failed";
        setError(message);
        throw err;
      }
    },
    []
  );

  const mutations = useFamilyMutations({
    orgId,
    userId,
    token: token ?? undefined,
    children,
    setChildrenCache,
    invalidate,
    runMutation,
  });

  const stats = useMemo(() => {
    const paymentCount = children.reduce(
      (n, c) => n + (c.schoolFees?.payments.length || 0),
      0
    );
    const activityCount = children.reduce(
      (n, c) => n + c.extracurriculars.length,
      0
    );
    const reportCardCount = children.reduce(
      (n, c) => n + (c.reportCards?.length || 0),
      0
    );
    return {
      childCount: children.length,
      paymentCount,
      activityCount,
      reportCardCount,
    };
  }, [children]);

  return {
    children,
    isHydrated,
    isSaving: mutations.isSaving,
    error: error || (query.error instanceof Error ? query.error.message : null),
    setError,
    stats,
    refresh: invalidate,
    upsertChild: mutations.upsertChild,
    deleteChild: mutations.deleteChild,
    updateChildCircles: mutations.updateChildCircles,
    upsertSchoolFees: mutations.upsertSchoolFees,
    recordPayment: mutations.recordPayment,
    deletePayment: mutations.deletePayment,
    deleteSchoolFees: mutations.deleteSchoolFees,
    upsertFeeAccount: mutations.upsertFeeAccount,
    deleteFeeAccount: mutations.deleteFeeAccount,
    cancelFeeAccount: mutations.cancelFeeAccount,
    recordFeeAccountPayment: mutations.recordFeeAccountPayment,
    deleteFeeAccountPayment: mutations.deleteFeeAccountPayment,
    markFeeAccountStatementSent: mutations.markFeeAccountStatementSent,
    setFeeAccountPaidInFull: mutations.setFeeAccountPaidInFull,
    markStatementSent: mutations.markStatementSent,
    setFeesPaidInFull: mutations.setFeesPaidInFull,
    upsertExtracurricular: mutations.upsertExtracurricular,
    addExtracurriculars: mutations.addExtracurriculars,
    deleteExtracurricular: mutations.deleteExtracurricular,
    upsertReportCard: mutations.upsertReportCard,
    deleteReportCard: mutations.deleteReportCard,
    addGuardian: mutations.addGuardian,
    removeGuardian: mutations.removeGuardian,
  };
}
